<template>
    <div id="content">
        <div class="container-fluid p-0">
            <div class="my-4">
                <div class="briefs-header">
                    <div>
                        <h2 class="briefs-title">{{ $t('briefs.title') }}</h2>
                        <p class="briefs-subtitle">{{ $t('briefs.subtitle') }}</p>
                    </div>
                    <button class="btn-create" @click="openCreate">
                        <i class="bi bi-plus-lg"></i>
                        <span>{{ $t('briefs.create') }}</span>
                    </button>
                </div>

                <div v-if="loading" class="briefs-loading">
                    <div class="spinner-border" role="status"></div>
                </div>

                <div v-else-if="!briefs.length" class="briefs-empty">
                    {{ $t('briefs.empty') }}
                </div>

                <table v-else class="table briefs-table">
                    <thead>
                        <tr>
                            <th>{{ $t('briefs.col.created') }}</th>
                            <th>{{ $t('briefs.col.client') }}</th>
                            <th>{{ $t('briefs.col.status') }}</th>
                            <th>{{ $t('briefs.col.link') }}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="b in briefs" :key="b.id">
                            <td>{{ fmtDate(b.created_at) }}</td>
                            <td>
                                <div>{{ b.account_email || prefillEmail(b) || '—' }}</div>
                                <small v-if="b.deal_id" class="text-muted">CRM #{{ b.deal_id }}</small>
                            </td>
                            <td>
                                <span class="brief-badge" :class="'brief-badge--' + badgeKind(b.status)">
                                    {{ statusLabel(b.status) }}
                                </span>
                            </td>
                            <td class="brief-link-cell">
                                <code class="brief-link">{{ b.url }}</code>
                                <button class="btn btn-sm btn-outline-primary" @click="copy(b)">
                                    {{ copied === b.id ? $t('briefs.copied') : $t('briefs.copy') }}
                                </button>
                            </td>
                            <td class="text-end">
                                <a v-if="b.status === 'built' && b.result && b.result.link"
                                   :href="b.result.link" target="_blank" rel="noopener"
                                   class="btn btn-sm btn-outline-secondary">{{ $t('briefs.openResult') }}</a>
                                <button v-if="['built', 'needs_attention', 'failed'].includes(b.status)"
                                        class="btn btn-sm btn-outline-secondary" @click="rebuild(b)">
                                    {{ $t('briefs.rebuild') }}
                                </button>
                                <button v-if="!['revoked', 'expired', 'built'].includes(b.status)"
                                        class="btn btn-sm btn-outline-danger" @click="revoke(b)">
                                    {{ $t('briefs.revoke') }}
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Создание брифа -->
        <div v-if="showCreate" class="brief-modal-backdrop" @click.self="showCreate = false">
            <div class="brief-modal">
                <h5>{{ $t('briefs.create') }}</h5>
                <div class="mb-2">
                    <label class="form-label">E-mail</label>
                    <input v-model="form.email" type="email" class="form-control" />
                </div>
                <div class="mb-2">
                    <label class="form-label">{{ $t('briefs.form.name') }}</label>
                    <input v-model="form.name" class="form-control" />
                </div>
                <div class="mb-2">
                    <label class="form-label">{{ $t('briefs.form.phone') }}</label>
                    <input v-model="form.phone" class="form-control" placeholder="+7" />
                </div>
                <div class="mb-2">
                    <label class="form-label">{{ $t('briefs.form.company') }}</label>
                    <input v-model="form.company" class="form-control" />
                </div>
                <div class="mb-3">
                    <label class="form-label">{{ $t('briefs.form.plan') }}</label>
                    <select v-model="form.plan_id" class="form-select">
                        <option :value="null">Free</option>
                        <option v-for="p in plans" :key="p.id" :value="p.id">{{ p.title || p.name }}</option>
                    </select>
                </div>
                <div v-if="createErr" class="alert alert-danger py-2">{{ createErr }}</div>
                <div class="d-flex justify-content-end gap-2">
                    <button class="btn btn-outline-secondary" @click="showCreate = false">{{ $t('briefs.cancel') }}</button>
                    <button class="btn btn-primary" :disabled="creating" @click="create">{{ $t('briefs.createConfirm') }}</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from '../../api.js'

const STATUS_KIND = {
    built: 'ok', needs_attention: 'warn', failed: 'err', revoked: 'err', expired: 'err',
}

export default {
    name: 'Briefs',
    data() {
        return {
            loading: true,
            briefs: [],
            plans: [],
            showCreate: false,
            creating: false,
            createErr: '',
            copied: null,
            form: { email: '', name: '', phone: '', company: '', plan_id: null },
        }
    },
    async mounted() {
        await this.load()
    },
    methods: {
        async load() {
            this.loading = true
            const r = await api.getBriefs()
            this.briefs = Array.isArray(r?.briefs) ? r.briefs : []
            this.loading = false
        },
        async openCreate() {
            this.createErr = ''
            this.form = { email: '', name: '', phone: '', company: '', plan_id: null }
            this.showCreate = true
            if (!this.plans.length) {
                const r = await api.getPlansWithLimits()
                this.plans = r?.data ?? []
            }
        },
        async create() {
            this.creating = true
            this.createErr = ''
            const r = await api.createBrief(this.form)
            this.creating = false
            if (r?.error) {
                this.createErr = r.error
                return
            }
            this.showCreate = false
            await this.load()
        },
        async revoke(b) {
            if (!confirm(this.$t('briefs.revokeConfirm'))) return
            await api.revokeBrief(b.id)
            await this.load()
        },
        async rebuild(b) {
            await api.rebuildBrief(b.id)
            await this.load()
        },
        copy(b) {
            navigator.clipboard.writeText(b.url).then(() => {
                this.copied = b.id
                setTimeout(() => { this.copied = null }, 1500)
            })
        },
        statusLabel(st) {
            return this.$t('briefs.status.' + st) !== 'briefs.status.' + st
                ? this.$t('briefs.status.' + st)
                : st
        },
        badgeKind(st) {
            return STATUS_KIND[st] ?? 'info'
        },
        prefillEmail(b) {
            // by-deal view не несёт prefill; для брифов из админки email в account_email после шага 1
            return ''
        },
        fmtDate(iso) {
            const d = new Date(iso)
            return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('ru-RU')
        },
    },
}
</script>

<style scoped>
.briefs-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1.2rem; }
.briefs-title { font-size: 1.3rem; font-weight: 700; margin: 0; }
.briefs-subtitle { color: #6b7280; font-size: .85rem; margin: .2rem 0 0; }
.btn-create { display: inline-flex; align-items: center; gap: .4rem; background: #4f46e5; color: #fff; border: none; border-radius: 8px; padding: .5rem 1rem; font-weight: 600; }
.btn-create:hover { background: #4338ca; color: #fff; }
.briefs-loading { text-align: center; padding: 3rem 0; }
.briefs-empty { color: #6b7280; padding: 2rem 0; text-align: center; }
.briefs-table { font-size: .87rem; vertical-align: middle; }
.brief-badge { display: inline-block; border-radius: 999px; padding: .15rem .6rem; font-size: .72rem; font-weight: 700; background: #eef2ff; color: #4338ca; }
.brief-badge--ok { background: #f0fdf4; color: #166534; }
.brief-badge--warn { background: #fef9c3; color: #854d0e; }
.brief-badge--err { background: #fef2f2; color: #991b1b; }
.brief-link-cell { display: flex; align-items: center; gap: .5rem; }
.brief-link { max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: inline-block; background: #f3f4f6; padding: .25rem .5rem; border-radius: 6px; }
.brief-modal-backdrop { position: fixed; inset: 0; background: rgba(15, 23, 42, .4); display: flex; align-items: center; justify-content: center; z-index: 1050; }
.brief-modal { background: #fff; border-radius: 12px; padding: 1.4rem; width: 420px; max-width: 92vw; }
</style>
